import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CircleCheck, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { Gender, PersonModel } from '@/common/models/person'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type EditProfileParams = {
  birthDate?: string
  documentNumber?: string
  name?: string
  genre?: Gender
  socialName?: string
}

async function editProfile({
  id,
  ...data
}: { id: string } & Partial<PersonModel>) {
  return api.backend.put(`/person/${id}`, data)
}

export function useEditProfile() {
  const { t } = useTranslation('profile')
  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    { id: string } & Partial<PersonModel>
  >({
    mutationKey: ['/person/id'],
    mutationFn: editProfile,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CircleCheck,
          title: t('general_data.toast.data.success.title'),
          description: t('general_data.toast.data.success.message'),
        },
      })
      queryClient.refetchQueries({ queryKey: ['getUserInfo'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('general_data.toast.data.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('general_data.toast.data.error.message'),
        },
      })
    },
  })

  const handleEditProfile = async (id: string, data: EditProfileParams) => {
    await mutateAsync({
      id,
      ...data,
    })
  }

  return {
    handleEditProfile,
    isPendingEditProfile: isPending,
  }
}
